import axios from "axios";

// Routage : BRouter (OpenStreetMap, gratuit, sans clé)
export const BROUTER_URL = "https://brouter.de/brouter";

/*
 * Modes de déplacement proposés par le toggle de l'onglet intendance.
 * Le profil BRouter change le tracé ET le temps estimé (le vélo emprunte
 * pistes cyclables et sens autorisés, la voiture suit les routes).
 */
export const ROUTING_MODES = {
  // riderEmoji : picto du livreur en mouvement (marqueur de position sur la carte)
  bike: { key: "bike", label: "Vélo", travelLabel: "à vélo", emoji: "🚲", riderEmoji: "🚴", profile: "trekking" },
  car: { key: "car", label: "Voiture", travelLabel: "en voiture", emoji: "🚗", riderEmoji: "🚗", profile: "car-fast" },
};

export const DEFAULT_ROUTING_MODE = "bike";

const profileFor = (mode) => (ROUTING_MODES[mode] || ROUTING_MODES[DEFAULT_ROUTING_MODE]).profile;

/*
 * Cache au niveau du module (et non par composant) : un tracé est réutilisé par la
 * carte ET par l'estimation de position du livreur, et survit au démontage de l'onglet.
 *
 * ⚠️ La clé inclut le mode : sans ça, passer de vélo à voiture resservirait le tracé
 * vélo déjà en cache.
 *
 * valeur `null` = routage indisponible (on retombe sur la ligne droite),
 * valeur absente = pas encore calculé.
 */
const routeCache = new Map();
const routePending = new Map();

const routeKeyFor = (latlngs, mode) =>
  `${mode}:${latlngs.map((coord) => coord.join(",")).join(";")}`;

/**
 * Tracé déjà connu pour ces waypoints et ce mode, sans déclencher d'appel réseau.
 * @returns {{latlngs: Array, minutes: number, km: number}|null|undefined}
 *          `null` si indisponible, `undefined` si inconnu
 */
export const getCachedRoute = (latlngs, mode) => {
  const key = routeKeyFor(latlngs, mode);
  return routeCache.has(key) ? routeCache.get(key) : undefined;
};

// Appel BRouter brut, sans cache ni repli
const fetchDirect = (latlngs, mode) => {
  const lonlats = latlngs.map(([lat, lng]) => `${lng},${lat}`).join("|");
  return axios
    .get(BROUTER_URL, {
      params: {
        lonlats: lonlats,
        profile: profileFor(mode),
        alternativeidx: 0,
        format: "geojson",
      },
    })
    .then((response) => {
      const feature = response.data && response.data.features && response.data.features[0];
      return feature
        ? {
            latlngs: feature.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
            minutes: Math.max(1, Math.round(feature.properties["total-time"] / 60)),
            km: feature.properties["track-length"] / 1000,
          }
        : null;
    })
    .catch(() => null);
};

/*
 * Repli segment par segment : BRouter échoue sur TOUTE une requête multi-waypoints
 * dès qu'UN segment est non routable (« no track found ») — fréquent en voiture,
 * les points de retrait étant souvent posés sur des zones piétonnes. On route alors
 * chaque paire de points séparément et on raccorde : seuls les segments réellement
 * non routables restent en ligne droite (avec un temps estimé à vitesse forfaitaire),
 * au lieu de perdre le tracé complet.
 */
const STRAIGHT_FALLBACK_KMH = { bike: 14, car: 25 };

const stitchSegments = (latlngs, mode) => {
  const pairs = latlngs.slice(0, -1).map((from, index) => [from, latlngs[index + 1]]);
  return Promise.all(pairs.map((pair) => fetchRoute(pair, mode))).then((segments) => {
    // Tout est mort (BRouter down) : on garde le repli ligne droite global
    if (segments.every((segment) => segment === null)) {
      return null;
    }
    const stitched = { latlngs: [], minutes: 0, km: 0 };
    segments.forEach((segment, index) => {
      let part;
      if (segment) {
        part = segment.latlngs;
        stitched.minutes += segment.minutes;
        stitched.km += segment.km;
      } else {
        // Segment non routable : ligne droite locale + estimation forfaitaire
        part = pairs[index];
        const km = distanceBetween(pairs[index][0], pairs[index][1]) / 1000;
        stitched.km += km;
        stitched.minutes += Math.round((km / (STRAIGHT_FALLBACK_KMH[mode] || 15)) * 60);
      }
      // Évite de doubler le point de jonction entre deux segments
      stitched.latlngs.push(...(stitched.latlngs.length > 0 ? part.slice(1) : part));
    });
    return stitched;
  });
};

/**
 * Itinéraire passant par tous les waypoints, dans le mode demandé.
 * Un seul appel par tracé (les appels concurrents partagent la même promesse).
 * Si la requête complète échoue, retente segment par segment (cf stitchSegments).
 * @param {Array} latlngs waypoints [[lat, lng], ...]
 * @param {string} mode clé de ROUTING_MODES ('bike' | 'car')
 * @returns {Promise<{latlngs: Array, minutes: number, km: number}|null>} `null` si BRouter est indisponible
 */
export const fetchRoute = (latlngs, mode) => {
  const key = routeKeyFor(latlngs, mode);

  if (routeCache.has(key)) {
    return Promise.resolve(routeCache.get(key));
  }
  if (routePending.has(key)) {
    return routePending.get(key);
  }

  const promise = fetchDirect(latlngs, mode)
    .then((route) => {
      if (route || latlngs.length <= 2) {
        return route;
      }
      return stitchSegments(latlngs, mode);
    })
    .then((route) => {
      routeCache.set(key, route);
      routePending.delete(key);
      return route;
    });

  routePending.set(key, promise);
  return promise;
};

// Distance approchée en mètres (équirectangulaire) : suffisant à l'échelle d'une tournée
const distanceBetween = ([lat1, lng1], [lat2, lng2]) => {
  const toRad = Math.PI / 180;
  const x = (lng2 - lng1) * toRad * Math.cos(((lat1 + lat2) / 2) * toRad);
  const y = (lat2 - lat1) * toRad;
  return Math.sqrt(x * x + y * y) * 6371000;
};

/**
 * Point situé à `fraction` du parcours (0 = départ, 1 = arrivée), mesuré en distance.
 * Le livreur est supposé à vitesse constante : c'est une position théorique.
 * @param {Array} latlngs géométrie du tracé (route.latlngs)
 * @returns {{lat: number, lng: number}|null}
 */
export const pointAlongRoute = (latlngs, fraction) => {
  if (!latlngs || latlngs.length === 0) {
    return null;
  }
  if (latlngs.length === 1) {
    return { lat: latlngs[0][0], lng: latlngs[0][1] };
  }

  const clamped = Math.min(1, Math.max(0, fraction));

  const cumulative = [0];
  for (let i = 1; i < latlngs.length; i++) {
    cumulative.push(cumulative[i - 1] + distanceBetween(latlngs[i - 1], latlngs[i]));
  }

  const total = cumulative[cumulative.length - 1];
  if (total === 0) {
    return { lat: latlngs[0][0], lng: latlngs[0][1] };
  }

  const target = clamped * total;
  let index = 1;
  while (index < cumulative.length - 1 && cumulative[index] < target) {
    index++;
  }

  const segmentStart = cumulative[index - 1];
  const segmentLength = cumulative[index] - segmentStart;
  const segmentFraction = segmentLength > 0 ? (target - segmentStart) / segmentLength : 0;

  const [lat1, lng1] = latlngs[index - 1];
  const [lat2, lng2] = latlngs[index];
  return {
    lat: lat1 + segmentFraction * (lat2 - lat1),
    lng: lng1 + segmentFraction * (lng2 - lng1),
  };
};
